import { useState, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Trash2,
  Calendar,
  User,
  Package,
  FileText,
  ExternalLink,
  Edit,
  Image
} from 'lucide-react';
import type { TodoWithSource, TodoTag, TodoSourceType } from '../../types/todo';
import { TODO_TAG_LABELS, TODO_TAG_COLORS, TODO_SOURCE_TYPE_LABELS } from '../../types/todo';

interface ToDoItemProps {
  todo: TodoWithSource;
  onToggle: (id: string, completed: boolean) => void;
  onDelete: (id: string) => void;
  onEdit: (todo: TodoWithSource) => void;
  isFocused?: boolean;
}

const SOURCE_ICONS: Record<TodoSourceType, React.ComponentType<{ className?: string }>> = {
  lead: User,
  product: Package,
  form: FileText,
  website: FileText,
  media: Image,
};

function getSourceLink(type: TodoSourceType, id: string, slug?: string): string {
  switch (type) {
    case 'lead':
      return `/leads/${id}`;
    case 'product':
      return `/products/${slug || id}`;
    case 'form':
      return `/forms/${id}`;
    case 'website':
      return `/site/pages`;
    case 'media':
      return `/media`;
    default:
      return '#';
  }
}

export const ToDoItem = forwardRef<HTMLDivElement, ToDoItemProps>(function ToDoItem(
  { todo, onToggle, onDelete, onEdit, isFocused },
  ref
) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleToggle = () => {
    onToggle(todo.id, !todo.completed);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(todo.id);
    } catch {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    });
  };

  const tagColors = todo.tag ? TODO_TAG_COLORS[todo.tag as TodoTag] : null;
  const tagLabel = todo.tag ? TODO_TAG_LABELS[todo.tag as TodoTag] : null;

  return (
    <div
      ref={ref}
      className={`
        group flex rounded-xl transition-all overflow-hidden
        ${isFocused
          ? 'bg-white dark:bg-slate-800 border-2 border-primary ring-2 ring-primary/30 shadow-lg'
          : todo.completed
            ? 'bg-gray-50 dark:bg-slate-700/50'
            : 'bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md'
        }
        ${isDeleting ? 'opacity-50' : ''}
      `}
    >
      {/* Main content area */}
      <div className="flex-1 flex items-start gap-3 p-4">
        {/* Checkbox */}
        <button
          onClick={handleToggle}
          className={`
            flex-shrink-0 w-6 h-6 rounded-lg border-2 transition-all flex items-center justify-center mt-0.5
            ${
              todo.completed
                ? 'bg-primary border-primary text-white'
                : 'border-gray-300 dark:border-slate-500 hover:border-primary'
            }
          `}
        >
          {todo.completed && <Check className="w-4 h-4" />}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title row with tag and date */}
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className={`
                font-medium text-[#1A1A1A] dark:text-white leading-tight
                ${todo.completed ? 'line-through text-gray-400 dark:text-slate-500' : ''}
              `}
            >
              {todo.title}
            </p>
            {tagLabel && tagColors && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tagColors.bg} ${tagColors.text}`}
              >
                {tagLabel}
              </span>
            )}
            <span className={`inline-flex items-center gap-1 text-xs ${todo.completed ? 'text-gray-400 dark:text-slate-500' : 'text-[#6B6B6B] dark:text-slate-400'}`}>
              <Calendar className="w-3 h-3" />
              {formatDate(todo.due_date)}
            </span>
          </div>

          {/* Description */}
          {todo.description && (
            <p
              className={`
                mt-1 text-sm leading-snug line-clamp-2
                ${todo.completed ? 'text-gray-400 dark:text-slate-500' : 'text-[#6B6B6B] dark:text-slate-400'}
              `}
            >
              {todo.description}
            </p>
          )}

          {/* Source link */}
          {todo.source && (
            <Link
              to={getSourceLink(todo.source.type, todo.source.id, todo.source.slug)}
              className={`
                mt-2 flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                ${todo.completed
                  ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500'
                  : 'bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 text-[#6B6B6B] dark:text-slate-400'
                }
              `}
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const SourceIcon = SOURCE_ICONS[todo.source.type];
                return <SourceIcon className="w-4 h-4 flex-shrink-0" />;
              })()}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-primary">
                  {TODO_SOURCE_TYPE_LABELS[todo.source.type]}
                </p>
                <p className="text-sm font-medium truncate dark:text-slate-300">
                  {todo.source.name}
                </p>
                {todo.source.subtitle && (
                  <p className="text-xs truncate opacity-75">
                    {todo.source.subtitle}
                  </p>
                )}
              </div>
              <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
            </Link>
          )}
        </div>
      </div>

      {/* Action buttons - right side, full height */}
      <div className="flex flex-shrink-0 border-l border-gray-100 dark:border-slate-700">
        {/* Edit button */}
        <button
          onClick={() => onEdit(todo)}
          className="w-10 flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-primary hover:bg-primary/10 transition-all border-r border-gray-100 dark:border-slate-700"
          title="Bearbeiten"
        >
          <Edit className="w-4 h-4" />
        </button>

        {/* Delete button */}
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="w-10 flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          title="Löschen"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
});
